import { ChangeDetectionStrategy, Component, inject, input, output } from '@angular/core';
import { DatePipe } from '@angular/common';
import { ServiceSelectDataItem, ConfigService } from '@assist/shared/data';
import { ReservationDetailResourceTabComponent, ServiceSelectComponent } from '@assist/info-tabs';
import { ButtonComponent } from '@cue/assist/ui/buttons';
import { fromLocalized, OrderItemDescriptor } from '@cue/api';
import { LoadingComponent } from '@cue/loading';
import { translation } from '@cue/translations';
import { cueAdd, cueArrowDown, cueArrowUp, cueDelete, IconComponent } from '@cue/ui/icons';
import { RtlDirective } from '@cue/utilities';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { NewReservationLocationDetailOrderRow, NewReservationOrderItemDraft } from '../new-reservation-dialog.models';

@Component({
  selector: 'assist-new-reservation-location-detail-accordion-item',
  templateUrl: './new-reservation-location-detail-accordion-item.component.html',
  styleUrl: './new-reservation-location-detail-accordion-item.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ButtonComponent,
    DatePipe,
    IconComponent,
    LoadingComponent,
    ReservationDetailResourceTabComponent,
    RtlDirective,
    ServiceSelectComponent,
    TranslocoPipe,
  ],
})
export class NewReservationLocationDetailAccordionItemComponent {
  readonly row = input.required<NewReservationLocationDetailOrderRow>();
  readonly editable = input.required<boolean>();
  readonly eventStart = input.required<Date>();
  readonly ordersEnabled = input.required<boolean>();
  readonly previousOrdersLoading = input.required<boolean>();

  readonly toggle = output<number>();
  readonly addingOrderItemChange = output<{ resourceIdForFiltering: number; adding: boolean }>();
  readonly orderItemNoteChange = output<{ resourceIdForFiltering: number; clientId: string; note: string }>();
  readonly orderItemRemove = output<{ resourceIdForFiltering: number; clientId: string }>();
  readonly orderNoteChange = output<{ resourceIdForFiltering: number; note: string }>();
  readonly orderServiceSelected = output<{ resourceIdForFiltering: number; item: ServiceSelectDataItem }>();

  private readonly configService = inject(ConfigService);
  private readonly translocoService = inject(TranslocoService);

  readonly translation = translation;
  readonly icons = {
    cueAdd,
    cueArrowDown,
    cueArrowUp,
    cueDelete,
  };

  onToggle() {
    this.toggle.emit(this.row().resource.idForFiltering);
  }

  onOrderNoteChange(note: string) {
    this.orderNoteChange.emit({ resourceIdForFiltering: this.row().resource.idForFiltering, note });
  }

  onOrderItemNoteChange(clientId: string, note: string) {
    this.orderItemNoteChange.emit({
      resourceIdForFiltering: this.row().resource.idForFiltering,
      clientId,
      note,
    });
  }

  onRemoveOrderItem(event: Event, clientId: string) {
    event.preventDefault();
    this.orderItemRemove.emit({ resourceIdForFiltering: this.row().resource.idForFiltering, clientId });
  }

  onSetAddingOrderItem(adding: boolean) {
    this.addingOrderItemChange.emit({ resourceIdForFiltering: this.row().resource.idForFiltering, adding });
  }

  onAddOrderItem(event: Event) {
    event.preventDefault();
    this.onSetAddingOrderItem(true);
  }

  onOrderServiceSelected(item: ServiceSelectDataItem) {
    this.orderServiceSelected.emit({ resourceIdForFiltering: this.row().resource.idForFiltering, item });
  }

  isOrderItemNoteInvalid(item: NewReservationOrderItemDraft): boolean {
    return item.noteAllowed && item.noteMinLength > 0 && item.note.trim().length < item.noteMinLength;
  }

  getPreviousOrderItemName(item: OrderItemDescriptor): string {
    const service = this.configService.value.services.find((serviceItem) => serviceItem.id === item.serviceId);

    return service ? this.getLocalizedName(service.name) : `#${item.serviceId}`;
  }

  getPreviousOrderItemImageUrl(item: OrderItemDescriptor): string {
    const service = this.configService.value.services.find((serviceItem) => serviceItem.id === item.serviceId);

    return service?.imageUrl || 'assets/images/placeholder-order.svg';
  }

  private getLocalizedName(value: Parameters<typeof fromLocalized>[0]): string {
    return fromLocalized(value, this.translocoService.getActiveLang(), this.translocoService.getDefaultLang()) ?? '';
  }
}
